// ── Step3 Render — Pure DOM writes, no state.js, no API ─────────
// All functions receive data as parameters. No getState, no fetch.

import {
  normalizeText,
  formatVolume,
} from '../../../utils.js';
import { renderListingReviewStripInto } from '../../../modules/listing/step3/listing-review-strip.js';
import { resolveSearchTeamRetryAvailability } from '../../../step3/search-team-retry.js';
import { $, resolveSnapshotImageUrl, readDeterministicChecks } from './vm.js';

function createStep3Element(tagName, className, text) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== undefined && text !== null) element.textContent = text;
  return element;
}

function appendTrafficKeywordText(parent, value) {
  const text = String(value ?? '');
  const keywordPattern = /★([^★]+)★/g;
  let cursor = 0;
  let match;
  while ((match = keywordPattern.exec(text)) !== null) {
    if (match.index > cursor) parent.append(document.createTextNode(text.slice(cursor, match.index)));
    parent.append(createStep3Element('span', 'traffic-kw-inline', match[1]));
    cursor = match.index + match[0].length;
  }
  if (cursor < text.length || !parent.childNodes.length) {
    parent.append(document.createTextNode(text.slice(cursor)));
  }
}

function appendBulletRichText(parent, value) {
  const text = String(value ?? '');
  const markerPattern = /【([^】]+)】|★([^★]+)★/g;
  let cursor = 0;
  let match;
  while ((match = markerPattern.exec(text)) !== null) {
    if (match.index > cursor) parent.append(document.createTextNode(text.slice(cursor, match.index)));
    parent.append(createStep3Element(
      'span',
      match[1] !== undefined ? 'bullet-highlight-tag' : 'traffic-kw-inline',
      match[1] !== undefined ? `【${match[1]}】` : match[2],
    ));
    cursor = match.index + match[0].length;
  }
  if (cursor < text.length || !parent.childNodes.length) {
    parent.append(document.createTextNode(text.slice(cursor)));
  }
}

function createCopyButton(datasetKey, datasetValue, title, text = '📋') {
  const button = createStep3Element('button', 'btn-copy-inline', text);
  button.type = 'button';
  button.title = title;
  button.dataset[datasetKey] = String(datasetValue ?? '');
  return button;
}

function createSmartBadge({ compact = false } = {}) {
  return createStep3Element('span', `badge-smart${compact ? ' badge-smart-compact' : ''}`, 'Smart AI 增强');
}

function createResultSectionHeader(title) {
  const header = createStep3Element('div', 'result-section-header');
  header.append(createStep3Element('span', 'result-section-title', title));
  return header;
}

function createV1CompareToggle(section, contentNodes) {
  const toggle = createStep3Element('div', 'v1-compare-toggle');
  toggle.dataset.v1Toggle = section;
  const label = createStep3Element('span', 'v1-toggle-label', '▶ 原始版本 (V1)');
  const content = createStep3Element('div', 'v1-compare-content');
  content.hidden = true;
  content.append(...contentNodes);
  toggle.append(label, content);
  return toggle;
}

function createEmptyGenerationPrompt({ qa = false } = {}) {
  const prompt = createStep3Element('div', 'empty-generation-prompt');
  const paragraph = document.createElement('p');
  if (qa) {
    paragraph.append(
      document.createTextNode('当前草稿暂无 '),
      createStep3Element('strong', '', 'Q&A'),
      document.createTextNode(' 内容。可重新生成补齐，或先进入 AI 优化中心查看诊断建议。'),
    );
  } else {
    paragraph.append(
      document.createTextNode('点击上方「'),
      createStep3Element('strong', '', '生成 Listing'),
      document.createTextNode('」按钮，基于竞品数据和关键词词库，生成优化后的完整 Listing 文案。'),
    );
  }
  prompt.append(createStep3Element('div', 'empty-prompt-icon', '✦'), paragraph);
  return prompt;
}

function createIssueRow({ type = 'error', label, message }) {
  const row = createStep3Element('div', `step3-issue-row ${type}`);
  row.append(
    createStep3Element('span', 'step3-issue-section', label),
    createStep3Element('span', 'step3-issue-text', message),
  );
  return row;
}

function createDiagnosticTags(values, color = 'gray') {
  const tags = createStep3Element('div', 'opt-diag-tags');
  tags.append(...values.map((value) => createStep3Element('span', `opt-diag-tag ${color}`, value)));
  return tags;
}

function createDiagnosticItem(label, values, color = 'gray') {
  const item = createStep3Element('div', 'opt-diag-item');
  item.append(createStep3Element('div', 'opt-diag-label', label), createDiagnosticTags(values, color));
  return item;
}

// ── Competitor Card ──────────────────────────────────────────────

export function renderCompetitorCard(snapshot, list, included) {
  if (!snapshot) return;

  $('#card-asin').textContent = snapshot.asin || '—';
  $('#card-brand').textContent = snapshot.brandEn || snapshot.brand || '—';
  $('#card-rating').textContent = snapshot.rating ? Number(snapshot.rating).toFixed(1) : '—';
  $('#card-reviews').textContent = snapshot.reviews ? formatVolume(snapshot.reviews) : '—';
  $('#card-price').textContent = snapshot.price ? `$${snapshot.price}` : '—';
  $('#card-bsr').textContent = snapshot.bsrRank ? `#${formatVolume(snapshot.bsrRank)}` : '—';

  const catPath = Array.isArray(snapshot.categoryPathEn) ? snapshot.categoryPathEn : [];
  $('#card-category').textContent = catPath.join(' › ') || '—';

  $('#snapshot-asin-badge').textContent = snapshot.asin || '—';
  $('#snapshot-brand-badge').textContent = snapshot.brandEn || snapshot.brand || '—';

  const strip = $('#asin-image-strip');
  if (strip) {
    strip.replaceChildren();
    const imgUrl = resolveSnapshotImageUrl(snapshot);
    if (imgUrl) {
      const img = document.createElement('img');
      img.src = imgUrl;
      img.alt = snapshot.asin || 'product';
      img.loading = 'lazy';
      img.className = 'competitor-card-image';
      img.addEventListener('error', () => { img.hidden = true; });
      strip.appendChild(img);
    }
  }

  renderScrapedSections(snapshot);
  renderCompetitorMultiBar(list, included);
}

export function renderScrapedSections(snapshot) {
  if (!snapshot) return;
  const container = $('#scraped-content-container');
  if (!container) return;
  const sections = [
    { label: '标题', key: 'titleEn', render: (value) => createStep3Element('div', '', value || '暂无') },
    { label: '五点描述', key: 'bulletsEn', render: (value) => {
      if (!Array.isArray(value) || !value.length) return createStep3Element('div', 'scraped-empty', '暂无');
      const list = createStep3Element('ol', 'text-list');
      list.append(...value.map((item) => createStep3Element('li', '', item)));
      return list;
    } },
    { label: '商品描述', key: 'descriptionEn', render: (value) => {
      const text = normalizeText(value);
      return createStep3Element('div', text ? 'scraped-description' : 'scraped-empty', text || '暂无');
    } },
    { label: '类目节点', key: 'categoryPathEn', render: (value) => {
      if (!Array.isArray(value) || !value.length) return createStep3Element('div', 'scraped-empty', '暂无');
      const path = createStep3Element('div', 'scraped-category-path');
      path.append(...value.map((item) => createStep3Element('span', 'chip', item)));
      return path;
    } },
  ];
  container.replaceChildren(...sections.map((section) => {
    const details = createStep3Element('details', 'scraped-item');
    const summary = createStep3Element('summary', 'scraped-item-header');
    summary.append(createStep3Element('span', 'scraped-item-title', section.label), createStep3Element('span', 'scraped-item-icon', '▶'));
    const body = createStep3Element('div', 'scraped-item-body');
    body.appendChild(section.render(snapshot[section.key]));
    details.append(summary, body);
    return details;
  }));
}

export function renderCompetitorMultiBar(list, included) {
  const bar = $('#competitor-multi-bar');
  if (!bar) return;
  if (!list || list.length <= 1) {
    bar.hidden = true;
    return;
  }
  bar.hidden = false;
  const hint = $('#competitor-merge-hint');
  if (hint) {
    const includedSize = included instanceof Set ? included.size : list.length;
    hint.textContent =
      `生成 Listing 将综合已勾选的 ${includedSize}/${list.length} 个竞品 ASIN：对比各竞品标题/五点/描述后，生成我方产品文案（不会照搬任一竞品品牌）。`;
  }
  const selIdx = window.__step3SelectedCompetitorIndex || 0;
  const tabs = $('#competitor-asin-tabs');
  if (tabs) {
    tabs.replaceChildren(...list.map((snap, index) => {
      const isActive = index === selIdx;
      const tab = createStep3Element('button', `competitor-tab${isActive ? ' active' : ''}`, snap.asin);
      tab.id = `competitor-tab-${index}`;
      tab.type = 'button';
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('aria-controls', 'competitor-snapshot-panel');
      tab.tabIndex = isActive ? 0 : -1;
      tab.dataset.competitorIndex = String(index);
      return tab;
    }));

    const panel = $('#competitor-snapshot-panel');
    if (panel) panel.setAttribute('aria-labelledby', `competitor-tab-${selIdx}`);
  }
  const row = $('#competitor-include-row');
  if (row) {
    const label = createStep3Element('span', 'competitor-include-label', '纳入生成：');
    row.replaceChildren(label, ...list.map((snap) => {
      const chip = createStep3Element('label', 'competitor-include-chip');
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.dataset.competitorAsin = String(snap.asin || '');
      input.checked = included instanceof Set && included.has(snap.asin);
      chip.append(input, createStep3Element('span', '', snap.asin));
      return chip;
    }));
  }
}

// ── Checklist ────────────────────────────────────────────────────

export function updateChecklist(viewModel) {
  const titleOk = viewModel?.title?.isAvailable;
  const bulletsOk = viewModel?.bullets?.isAvailable && viewModel.bullets.count >= 5;
  const stOk = viewModel?.searchTerms?.isAvailable;

  const checkTitle = $('#check-title');
  const checkBullets = $('#check-bullets');
  const checkSearchterms = $('#check-searchterms');

  updateChecklistItem(checkTitle, titleOk, 'Product Title');
  updateChecklistItem(checkBullets, bulletsOk, '五点描述');
  updateChecklistItem(checkSearchterms, stOk, '搜索词');
}

function updateChecklistItem(item, isReady, label) {
  if (!item) return;
  const status = isReady ? '已生成' : '待生成';
  item.className = 'check-item' + (isReady ? '' : ' pending');
  item.querySelector('.check-icon').textContent = isReady ? '✅' : '⏳';
  item.querySelector('.check-status').textContent = status;
  item.setAttribute('aria-label', `${label}：${status}`);
}

// ── Panel Builders ───────────────────────────────────────────────

export function buildOptimizerDiagnosisPanel(result) {
  const { missingCore = [], missingTrafficKeywords = [], lostWords = [], coveredCore = [] } = result;
  const panel = createStep3Element('div', 'result-section-card optimizer-diagnosis-panel');
  const header = createResultSectionHeader('🔬 关键词覆盖诊断');
  header.append(createStep3Element('span', 'badge-accent', '已优化'));
  const body = createStep3Element('div', 'opt-diag-body');
  const groups = [
    ['✅ 已在Listing中覆盖', coveredCore.slice(0, 10), 'green'],
    ['⚠️ 缺失核心词（已补入V2）', missingCore.slice(0, 8), 'red'],
    ['🔍 关键词机会', missingTrafficKeywords.slice(0, 8), 'orange'],
    ['❌ 流失词（已移除/否词）', lostWords.slice(0, 8), 'gray'],
  ];
  body.append(...groups
    .filter(([, keywords]) => keywords.length > 0)
    .map(([label, keywords, color]) => createDiagnosticItem(label, keywords, color)));
  panel.append(header, body);
  return panel;
}

export function buildGenerationIssuePanel(viewModel) {
  const issueItems = viewModel?.issueItems || [];
  const missingSections = viewModel?.missingSections || [];
  if (!issueItems.length && !missingSections.length && viewModel?.status !== 'partial') return null;

  const panel = createStep3Element('div', 'step3-generation-issues');
  const body = createStep3Element('div', 'step3-issues-body');
  if (missingSections.length) {
    body.append(createIssueRow({
      type: 'warning',
      label: '进入下一步',
      message: `还缺少：${missingSections.map((section) => section.label).join('、')}`,
    }));
  }
  body.append(...issueItems.map((item) => createIssueRow({
    type: item.type === 'warning' ? 'warning' : 'error',
    label: item.label,
    message: item.message,
  })));
  panel.append(createStep3Element('div', 'step3-issues-title', '生成结果需要确认'), body);
  return panel;
}

export function buildBusinessMetaPanel(meta) {
  const mergeMeta = meta?.mergeMeta || {};
  const business = mergeMeta.businessMeta || {};
  const keywordSummary = mergeMeta.keywordPolicySummary || business.keywordPolicySummary;
  const roleSummary = mergeMeta.competitorRoleSummary || business.competitorRoleSummary;
  const diagnosis = mergeMeta.businessDiagnosis;
  if (!keywordSummary && !roleSummary && !diagnosis && !business.degraded) return null;

  const keywordLabels = {
    must_use: '必须写入',
    can_use: '可写入',
    backend_only: '后台词',
    ads_only: '广告测试',
    forbidden: '禁用',
  };
  const roleLabels = {
    main_competitor: '主竞品',
    traffic_reference: '流量参考',
    copy_reference: '文案参考',
    image_reference: '图片参考',
    excluded: '排除',
  };
  const mustUse = (keywordSummary?.top?.must_use || []).slice(0, 6);
  const forbidden = (keywordSummary?.top?.forbidden || []).slice(0, 6);
  const versions = Array.isArray(mergeMeta.versions) ? mergeMeta.versions : [];

  const panel = createStep3Element('div', 'result-section-card business-meta-panel');
  const header = createResultSectionHeader('生成依据');
  header.append(createStep3Element('span', 'badge-accent', business.degraded ? '已降级' : '业务契约'));
  panel.append(header);
  if (business.degraded) {
    panel.append(createIssueRow({
      type: 'warning',
      label: '业务契约',
      message: business.error || '业务元数据生成失败，已回退旧生成逻辑',
    }));
  }
  const countTags = (counts, labels) => Object.entries(labels)
    .flatMap(([key, label]) => {
      const value = Number(counts?.[key] || 0);
      return value > 0 ? [`${label} ${value}`] : [];
    });
  if (keywordSummary) panel.append(createDiagnosticItem('关键词准入', countTags(keywordSummary.counts, keywordLabels)));
  if (mustUse.length) panel.append(createDiagnosticItem('优先覆盖词', mustUse.map((keyword) => keyword.keyword), 'green'));
  if (forbidden.length) panel.append(createDiagnosticItem('已阻断风险词', forbidden.map((keyword) => keyword.keyword), 'red'));
  if (roleSummary) panel.append(createDiagnosticItem('竞品角色', countTags(roleSummary.counts, roleLabels)));
  if (diagnosis?.summary) {
    const tags = createStep3Element('div', 'opt-diag-tags');
    tags.append(
      createStep3Element('span', `opt-diag-tag ${diagnosis.summary.criticalCount ? 'red' : 'gray'}`, `严重 ${Number(diagnosis.summary.criticalCount || 0)}`),
      createStep3Element('span', `opt-diag-tag ${diagnosis.summary.warningCount ? 'orange' : 'gray'}`, `警告 ${Number(diagnosis.summary.warningCount || 0)}`),
      createStep3Element('span', 'opt-diag-tag gray', `提示 ${Number(diagnosis.summary.infoCount || 0)}`),
    );
    const item = createStep3Element('div', 'opt-diag-item');
    item.append(createStep3Element('div', 'opt-diag-label', '四象限自省'), tags);
    panel.append(item);
  }
  if (versions.length) {
    panel.append(createDiagnosticItem('版本链路', versions.map((version) => `${version.versionId || ''} · ${version.versionType || ''}`)));
  }
  return panel;
}

// ── Deterministic Checks Panel (合规/评分/算法) ─────────────────
// 数据来自 mergeMeta.deterministicChecks（Step3 生成后自动前移）。
// 缺失/旧数据时返回 null，由调用方隐藏卡片（向后兼容）。

function createBangBadge(status) {
  const map = {
    ok: ['badge-accent', '已通过'],
    partial: ['badge-warn', '部分通过'],
    error: ['badge-danger', '检查失败'],
  };
  const [cls, text] = map[status] || map.error;
  return createStep3Element('span', cls, text);
}

function appendDiagnosticRow(panel, label, tags) {
  const item = createStep3Element('div', 'opt-diag-item');
  item.append(createStep3Element('div', 'opt-diag-label', label));
  const tagsEl = createStep3Element('div', 'opt-diag-tags');
  tagsEl.append(...tags);
  item.append(tagsEl);
  panel.append(item);
}

function countAlgorithmTips(tips) {
  if (!tips || typeof tips !== 'object') return 0;
  return Object.values(tips).reduce((count, list) => {
    return count + (Array.isArray(list) ? list.length : 0);
  }, 0);
}

function firstAlgorithmTip(tips) {
  if (!tips || typeof tips !== 'object') return null;
  for (const list of Object.values(tips)) {
    if (Array.isArray(list) && list.length && list[0]?.message) return list[0];
  }
  return null;
}

export function buildDeterministicChecksPanel(checks) {
  if (!checks || typeof checks !== 'object') return null;
  const overall = String(checks.status || 'error');
  const compliance = checks.compliance || {};
  const scoring = checks.scoring || {};
  const algorithm = checks.algorithm || {};

  const panel = createStep3Element('div', 'result-section-card deterministic-checks-card');
  const header = createResultSectionHeader('🧪 确定性质量检查');
  header.append(createBangBadge(overall));
  panel.append(header);

  if (overall === 'error' && checks.error) {
    panel.append(createStep3Element('p', 'muted', checks.error));
  }

  // 规则合规
  const cData = compliance.status === 'ok' && compliance.data && typeof compliance.data === 'object' ? compliance.data : null;
  const cTags = [];
  if (compliance.status === 'error') {
    cTags.push(createStep3Element('span', 'opt-diag-tag red', '合规检查失败'));
  } else if (cData) {
    const cScore = Number(cData.score);
    if (Number.isFinite(cScore)) {
      cTags.push(createStep3Element('span', 'opt-diag-tag gray', `合规分 ${cScore}/100`));
    }
    const vCount = Array.isArray(cData.violations) ? cData.violations.length : 0;
    cTags.push(createStep3Element('span', `opt-diag-tag ${vCount > 0 ? 'orange' : 'green'}`, `${vCount} 个合规风险`));
  }
  if (cTags.length) appendDiagnosticRow(panel, '规则合规', cTags);

  // 质量评分
  const sData = scoring.status === 'ok' && scoring.data && typeof scoring.data === 'object' ? scoring.data : null;
  const sTags = [];
  if (scoring.status === 'error') {
    sTags.push(createStep3Element('span', 'opt-diag-tag red', '评分检查失败'));
  } else if (sData) {
    const total = Number(sData.totalScore);
    const max = Number(sData.maxScore) || 100;
    if (Number.isFinite(total)) {
      sTags.push(createStep3Element('span', 'opt-diag-tag gray', `综合评分 ${total}/${max}`));
    }
    const sugCount = Array.isArray(sData.suggestions) ? sData.suggestions.length : 0;
    if (sugCount > 0) {
      sTags.push(createStep3Element('span', 'opt-diag-tag orange', `${sugCount} 条优化建议`));
    }
  }
  if (sTags.length) appendDiagnosticRow(panel, '质量评分', sTags);

  // 算法建议
  const aData = algorithm.status === 'ok' && algorithm.data && typeof algorithm.data === 'object' ? algorithm.data : null;
  const aTags = [];
  if (algorithm.status === 'error') {
    aTags.push(createStep3Element('span', 'opt-diag-tag red', '算法建议检查失败'));
  } else if (aData) {
    const tipCount = countAlgorithmTips(aData.tips);
    aTags.push(createStep3Element('span', 'opt-diag-tag gray', `${tipCount} 条算法建议`));
  }
  if (aTags.length) appendDiagnosticRow(panel, '算法建议', aTags);

  // 算法建议摘要（首条）
  if (aData) {
    const firstTip = firstAlgorithmTip(aData.tips);
    if (firstTip?.message) {
      panel.append(createStep3Element('p', 'muted deterministic-tip-summary', firstTip.message));
    }
  }

  return panel;
}


// ── Main Listing Render ──────────────────────────────────────────

export function renderGeneratedListing(viewModel, { titleState, bulletsState, descriptionState, stState, qaState, optimizerResult, generationMeta, sectionErrors, titleShowingZh, titleZhMap, selectedTitleVersion, listingDraftV1, bulletsShowingZh, bulletsZhMap, descriptionShowingZh, descriptionZhText, aplusShowingZh, aplusZhMap, searchTermsShowingZh, searchTermsZhText }) {
  const titleHighlightContainer = $('#step3-title-highlight-container');
  const bulletsContainer = $('#step3-bullets-container');
  const descriptionContainer = $('#step3-description-container');
  const searchTermsContainer = $('#step3-search-terms-container');
  const qaContainer = $('#step3-qa-container');
  const aplusContainer = $('#step3-aplus-container');

  if (!titleHighlightContainer) return;

  const hasTitle = viewModel?.title?.isAvailable;
  const hasBullets = viewModel?.bullets?.isAvailable;
  const hasDescription = viewModel?.description?.isAvailable;
  const hasAplus = viewModel?.aplusModules?.length > 0;
  const hasQa = viewModel?.qa?.isAvailable;
  const hasSearchTerms = viewModel?.searchTerms?.isAvailable;

  renderListingReviewStripInto($('#step3-review-strip'), {
    title: titleState,
    bullets: bulletsState,
    description: descriptionState,
    searchTerms: stState,
    qa: qaState,
    selectedTitleVersion,
    listingDraftV1,
    generationMeta,
    sectionErrors,
  });

  // ── V1 comparison data ──────────────────────────────────────────
  const v1Compare = viewModel?.v1Compare || {};

  const v1TitleSection = v1Compare.title?.shouldShow
    ? createV1CompareToggle('title', (() => {
      const text = createStep3Element('div', 'title-version-text v1-original-text');
      appendTrafficKeywordText(text, v1Compare.title.text);
      return [text];
    })())
    : null;

  const v1BulletsSection = v1Compare.bullets?.shouldShow
    ? createV1CompareToggle('bullets', (v1Compare.bullets.items || []).map((bullet, index) => {
      const item = createStep3Element('div', 'v1-bullet-item');
      item.append(createStep3Element('strong', '', `B${index + 1}: `));
      appendTrafficKeywordText(item, bullet);
      return item;
    }))
    : null;

  const v1DescSection = v1Compare.description?.shouldShow
    ? createV1CompareToggle('description', (() => {
      const text = createStep3Element('div', 'description-text v1-original-text');
      appendTrafficKeywordText(text, v1Compare.description.text);
      return [text];
    })())
    : null;

  // 1. ── Product Title & Item Highlights Pane ──────────────────────
  const titleRoot = createStep3Element('div', 'generated-sections');
  titleRoot.id = 'generated-listing-content';
  const issuePanel = buildGenerationIssuePanel(viewModel);
  const businessMetaPanel = buildBusinessMetaPanel(generationMeta);
  const deterministicPanel = buildDeterministicChecksPanel(readDeterministicChecks(generationMeta));

  if (hasTitle) {
    if (issuePanel) titleRoot.append(issuePanel);
    if (businessMetaPanel) titleRoot.append(businessMetaPanel);
    if (deterministicPanel) titleRoot.append(deterministicPanel);
    const options = viewModel.title.options || [];
    const showingZh = !!titleShowingZh;
    const zhMap = titleZhMap || {};
    const titleCard = createStep3Element('div', 'result-section-card');
    const titleHeader = createResultSectionHeader('📌 Product Title / Item Highlights（商品亮点）');
    if (titleState?.isSmartGenerated) titleHeader.append(createSmartBadge());
    if (options.length) titleHeader.append(createStep3Element('span', 'badge-accent', `${options.length}个版本`));
    const translateButton = createStep3Element('button', 'ghost-button step3-title-translate', showingZh ? '查看原文' : '中文理解');
    translateButton.id = 'translate-title';
    translateButton.type = 'button';
    titleHeader.append(translateButton);
    titleCard.append(titleHeader);

    if (options.length) {
      const versions = createStep3Element('div', 'title-versions');
      versions.append(...options.map((option) => {
        const isSelected = option.index === viewModel.title.selectedIndex;
        const card = createStep3Element('div', `title-version-card${isSelected ? ' selected' : ''}`);
        card.dataset.index = String(option.index);
        const header = createStep3Element('div', 'title-version-header');
        header.append(createStep3Element('span', 'version-tag', option.label));
        if (titleState?.isSmartGenerated) header.append(createSmartBadge({ compact: true }));
        header.append(createStep3Element('span', 'formula-tag', option.formula || '—'));
        const productTitleLabel = createStep3Element(
          'div',
          'mobile-preview-label',
          'Product Title（产品标题） · ≤75字符'
        );
        productTitleLabel.append(createCopyButton('copyTitle', option.index, '复制 Product Title'));
        const titleText = createStep3Element('div', 'title-version-text');
        appendTrafficKeywordText(titleText, option.text);
        card.append(header, productTitleLabel, titleText);
        const zhText = zhMap[option.index] || '';
        if (showingZh && zhText) card.append(createStep3Element('div', 'title-version-zh', zhText));
        const meta = createStep3Element('div', 'title-version-meta');
        const metaItem = createStep3Element('span', 'title-meta-item');
        metaItem.append(createStep3Element('span', 'emoji', '📝'), document.createTextNode(` ${option.charCount}/75 字符`));
        meta.append(metaItem);
        card.append(meta);

        const highlightText = option.itemHighlights ?? viewModel.title.itemHighlights ?? '';
        const highlight = createStep3Element('div', 'title-highlight-block');
        const highlightLabel = createStep3Element(
          'div',
          'mobile-preview-label',
          'Item Highlights（商品亮点） · ≤125字符'
        );
        highlightLabel.append(createCopyButton('copyHighlight', option.index, '复制 Item Highlights'));
        const highlightValue = createStep3Element('div', 'title-highlight-text');
        appendTrafficKeywordText(highlightValue, highlightText || '暂无 Item Highlights');
        highlight.append(highlightLabel, highlightValue);
        const translatedHighlight = zhMap[`hl_${option.index}`] || '';
        if (showingZh && highlightText) highlight.append(createStep3Element('div', 'title-version-zh title-highlight-zh', translatedHighlight));
        const highlightMeta = createStep3Element('div', 'title-version-meta');
        highlightMeta.append(createStep3Element('span', highlightText.length <= 125 ? 'stat-ok' : 'stat-warn', `${highlightText.length}/125 字符`));
        highlight.append(highlightMeta);
        card.append(highlight);

        if (option.tags?.length) {
          const tags = createStep3Element('div', 'title-version-tags');
          tags.append(...option.tags.map((tag) => createStep3Element('span', 'title-tag', tag)));
          card.append(tags);
        }
        const mobilePreview = createStep3Element('div', 'mobile-preview-block');
        const mobileText = createStep3Element('div', 'mobile-preview-text');
        appendTrafficKeywordText(mobileText, option.mobilePreview || option.text);
        mobilePreview.append(createStep3Element('div', 'mobile-preview-label', 'Product Title 手机端预览'), mobileText);
        card.append(mobilePreview);
        return card;
      }));
      titleCard.append(versions);
      if (v1TitleSection) titleCard.append(v1TitleSection);
    } else {
      const card = createStep3Element('div', 'title-version-card');
      const productTitleLabel = createStep3Element(
        'div',
        'mobile-preview-label',
        'Product Title（产品标题） · ≤75字符'
      );
      productTitleLabel.append(createCopyButton('copyTitle', 0, '复制 Product Title'));
      card.append(
        productTitleLabel,
        createStep3Element('div', 'title-version-text', viewModel.title.productTitle || '暂无 Product Title')
      );
      const zhText = zhMap[0] || '';
      if (showingZh && zhText) card.append(createStep3Element('div', 'title-version-zh', zhText));
      const highlight = createStep3Element('div', 'title-highlight-block');
      const highlightLabel = createStep3Element(
        'div',
        'mobile-preview-label',
        'Item Highlights（商品亮点） · ≤125字符'
      );
      highlightLabel.append(createCopyButton('copyHighlight', 0, '复制 Item Highlights'));
      const highlightText = viewModel.title.itemHighlights;
      highlight.append(
        highlightLabel,
        createStep3Element('div', 'title-highlight-text', highlightText || '暂无 Item Highlights')
      );
      if (showingZh && highlightText) highlight.append(createStep3Element('div', 'title-version-zh title-highlight-zh', zhMap.hl_0 || ''));
      card.append(highlight);
      titleCard.append(card);
    }
    titleRoot.append(titleCard);
  } else {
    titleRoot.append(createEmptyGenerationPrompt());
  }
  titleHighlightContainer.replaceChildren(titleRoot);

  // 2. ── Bullets Pane ──────────────────────────────────────────────
  const bulletsRoot = createStep3Element('div', 'generated-sections');
  if (hasBullets) {
    const items = viewModel.bullets.items || [];
    if (items.length) {
      const card = createStep3Element('div', 'result-section-card');
      const header = createResultSectionHeader('⚡ 五点');
      if (bulletsState?.isSmartGenerated) header.append(createSmartBadge());
      header.append(createStep3Element('span', 'badge-accent', `${items.length}条`));
      const translateBulletsButton = createStep3Element('button', 'ghost-button step3-title-translate', bulletsShowingZh ? '查看原文' : '中文理解');
      translateBulletsButton.id = 'translate-bullets';
      translateBulletsButton.type = 'button';
      header.append(translateBulletsButton);
      const list = createStep3Element('div', 'bullets-list');
      list.append(...items.map((item) => {
        const bullet = createStep3Element('div', 'bullet-item-card');
        const left = createStep3Element('div', 'bullet-left-col');
        left.append(createStep3Element('span', 'bullet-index-tag', item.label));
        const text = createStep3Element('div', 'bullet-text');
        appendBulletRichText(text, item.text);
        const right = createStep3Element('div', 'bullet-right-col');
        right.append(
          createStep3Element('span', 'bullet-bytes-tag', `${item.byteCount} 字节`),
          createCopyButton('copyBullet', item.index, '复制此要点'),
        );
        bullet.append(left, text, right);
        const bulletZh = (bulletsZhMap && bulletsZhMap[item.index]) || '';
        if (bulletsShowingZh && bulletZh) bullet.append(createStep3Element('div', 'title-version-zh bullet-zh', bulletZh));
        return bullet;
      }));
      card.append(header, list);
      if (v1BulletsSection) card.append(v1BulletsSection);
      bulletsRoot.append(card);
    }
  }

  if (optimizerResult) {
    bulletsRoot.append(buildOptimizerDiagnosisPanel(optimizerResult));
  }

  if (hasBullets || optimizerResult) {
    bulletsContainer.replaceChildren(bulletsRoot);
  } else {
    bulletsContainer.replaceChildren(createEmptyGenerationPrompt());
  }

  // 3. ── Description Pane ──────────────────────────────────────────
  if (hasDescription) {
    const paragraphs = viewModel.description.paragraphs || [];
    const card = createStep3Element('div', 'result-section-card');
    const header = createResultSectionHeader('📖 产品描述');
    if (descriptionState?.isSmartGenerated) header.append(createSmartBadge());
    const translateDescButton = createStep3Element('button', 'ghost-button step3-title-translate', descriptionShowingZh ? '查看原文' : '中文理解');
    translateDescButton.id = 'translate-description';
    translateDescButton.type = 'button';
    header.append(translateDescButton);
    header.append(createCopyButton('copyDescription', '', '复制描述'));
    const content = createStep3Element('div', 'description-box');
    const text = createStep3Element('div', 'description-text');
    text.append(...paragraphs.map((paragraph) => {
      const node = document.createElement('p');
      appendTrafficKeywordText(node, paragraph);
      return node;
    }));
    content.append(createStep3Element('div', 'description-label-tag', 'A+ Content 描述'), text);
    if (descriptionShowingZh && descriptionZhText) {
      content.append(createStep3Element('div', 'title-version-zh description-zh', descriptionZhText));
    }
    card.append(header, content);
    if (v1DescSection) card.append(v1DescSection);
    descriptionContainer.replaceChildren(card);
  } else {
    descriptionContainer.replaceChildren(createEmptyGenerationPrompt());
  }

  // 3. ── Search Terms Pane ─────────────────────────────────────────
  const searchTeamRetry = resolveSearchTeamRetryAvailability({
    searchTerms: stState,
    generationMeta,
  });
  // A completed Listing can legitimately have no safe backend terms. Present
  // that as a Search Terms result (and an optional retry), never as though the
  // user had not generated the Listing at all.
  const hasGeneratedListing = hasTitle || hasBullets || hasDescription || hasQa;
  if (hasSearchTerms || searchTeamRetry.visible || hasGeneratedListing) {
    const stModel = viewModel.searchTerms;
    const previewText = stModel.text;
    const charCount = stModel.charCount;
    const byteCount = stModel.byteCount;
    const stOk = !stModel.isOverLimit;

    const card = createStep3Element('div', 'result-section-card');
    const header = createResultSectionHeader('🔑 后台搜索词');
    if (hasSearchTerms) {
      const translateStButton = createStep3Element('button', 'ghost-button step3-title-translate', searchTermsShowingZh ? '查看原文' : '中文理解');
      translateStButton.id = 'translate-searchterms';
      translateStButton.type = 'button';
      header.append(translateStButton);
      header.append(createCopyButton('copySearchterms', '', '复制搜索词'));
    }
    if (searchTeamRetry.visible) {
      const retryButton = createStep3Element('button', 'ghost-button', '单独重试市场补词');
      retryButton.id = 'retry-search-team-button';
      retryButton.type = 'button';
      retryButton.dataset.taskId = searchTeamRetry.taskId;
      header.append(retryButton);
    }
    const resultBox = createStep3Element('div', 'search-terms-result-box');
    const stats = createStep3Element('div', 'search-terms-stats');
    stats.append(
      createStep3Element('span', charCount > 0 ? 'stat-ok' : '', `${charCount} 字符`),
      createStep3Element('span', stOk ? 'stat-ok' : 'stat-warn', `${byteCount} bytes${stOk ? ' ✅' : ' ⚠️'}`),
    );
    resultBox.append(createStep3Element(
      'div',
      'search-terms-result-text',
      previewText || '本轮 Listing 已生成，但当前没有可发布的后台搜索词。',
    ), stats);
    if (searchTermsShowingZh && searchTermsZhText) {
      resultBox.append(createStep3Element('div', 'title-version-zh searchterms-zh', searchTermsZhText));
    }
    card.append(header, resultBox);
    if (searchTeamRetry.visible && searchTeamRetry.message) {
      card.append(createStep3Element('p', 'muted', searchTeamRetry.message));
    } else if (!hasSearchTerms) {
      card.append(createStep3Element(
        'p',
        'muted',
        '后台词独立于正文生成；系统不会把空结果误显示成“尚未生成”。',
      ));
    }
    searchTermsContainer.replaceChildren(card);
  } else {
    searchTermsContainer.replaceChildren(createEmptyGenerationPrompt());
  }

  // 4. ── Q&A Pane ──────────────────────────────────────────────────
  if (hasQa) {
    const qaItems = viewModel.qa.items || [];
    const hasQaChinese = qaItems.length > 0 && qaItems.every((item) => (
      item.questionZh && item.answerZh
    ));
    const createQaLine = (rowClass, iconClass, iconText, textClass, value) => {
      const row = createStep3Element('div', rowClass);
      row.append(createStep3Element('span', iconClass, iconText), document.createTextNode(' '), createStep3Element('span', textClass, value));
      return row;
    };
    const card = createStep3Element('div', 'result-section-card qa-result-card');
    const header = createResultSectionHeader('✨ Rufus / Alexa 智能语义 QA (痛点解决场景)');
    const translateQaButton = createStep3Element(
      'button',
      'ghost-button',
      hasQaChinese ? '重新翻译 QA' : '翻译 QA',
    );
    translateQaButton.id = 'translate-qa-button';
    translateQaButton.type = 'button';
    translateQaButton.title = '仅翻译已生成的 QA，不重新生成或改写商品事实';
    header.append(translateQaButton);
    header.append(createCopyButton('copyQaModule', '', '复制全部 QA', '📋 复制全部 QA'));
    const list = createStep3Element('div', 'qa-modern-container');
    list.append(...qaItems.map((item) => {
      const qaCard = createStep3Element('div', 'qa-item-card glassmorphism-card');
      const qaHeader = createStep3Element('div', 'qa-item-header');
      const actions = createStep3Element('div', 'qa-actions');
      const copyPairButton = createStep3Element('button', 'btn-copy-qa-all-inline', '📋 复制');
      copyPairButton.type = 'button';
      copyPairButton.title = '复制英文问答';
      copyPairButton.dataset.copyQaEnglishPair = String(item.index);
      actions.append(copyPairButton);
      qaHeader.append(createStep3Element('span', 'qa-item-badge', `Q&A 对 ${item.index + 1}`), actions);
      const english = createStep3Element('div', 'qa-lang-block qa-lang-en');
      english.append(
        createQaLine('qa-q-row', 'q-icon', 'Q:', 'q-text', item.questionEn),
        createQaLine('qa-a-row', 'a-icon', 'A:', 'a-text', item.answerEn),
      );
      const bilingual = createStep3Element('div', 'qa-body-bilingual');
      bilingual.append(english);
      if (item.questionZh || item.answerZh) {
        const chinese = createStep3Element('div', 'qa-lang-block qa-lang-zh');
        chinese.append(
          createQaLine('qa-q-row', 'q-icon', '问:', 'q-text', item.questionZh),
          createQaLine('qa-a-row', 'a-icon', '答:', 'a-text', item.answerZh),
        );
        bilingual.append(createStep3Element('div', 'qa-lang-divider'), chinese);
      }
      qaCard.append(qaHeader, bilingual);
      return qaCard;
    }));
    card.append(header, list);
    qaContainer.replaceChildren(card);
  } else {
    qaContainer.replaceChildren(createEmptyGenerationPrompt({ qa: hasTitle || hasBullets }));
  }

  // 5. ── A+ Modules Pane ───────────────────────────────────────────
  if (hasAplus) {
    const modules = viewModel.aplusModules;
    const card = createStep3Element('div', 'result-section-card');
    const aplusHeader = createResultSectionHeader('🎨 A+ 内容模块');
    const translateAplusButton = createStep3Element('button', 'ghost-button step3-title-translate', aplusShowingZh ? '查看原文' : '中文理解');
    translateAplusButton.id = 'translate-aplus';
    translateAplusButton.type = 'button';
    aplusHeader.append(translateAplusButton);
    const grid = createStep3Element('div', 'aplus-grid');
    grid.append(...modules.map((module) => {
      const moduleCard = createStep3Element('div', 'aplus-item-card');
      const moduleHeader = createStep3Element('div', 'aplus-card-header');
      moduleHeader.append(
        createStep3Element('span', 'aplus-type-tag', module.moduleType),
        createCopyButton('copyAplus', module.index, '复制此模块'),
      );
      moduleCard.append(moduleHeader);
      if (module.headline) moduleCard.append(createStep3Element('div', 'aplus-headline', module.headline));
      if (module.bodyText) moduleCard.append(createStep3Element('div', 'aplus-body', module.bodyText));
      const aplusZh = (aplusZhMap && aplusZhMap[module.index]) || '';
      if (aplusShowingZh && aplusZh) moduleCard.append(createStep3Element('div', 'title-version-zh aplus-zh', aplusZh));
      const footer = createStep3Element('div', 'aplus-footer');
      footer.append(
        createStep3Element('span', 'aplus-position-icon', '🖼️'),
        createStep3Element('span', '', module.imageDirection || `位置 ${module.index + 1}`),
      );
      moduleCard.append(footer);
      return moduleCard;
    }));
    card.append(aplusHeader, grid);
    aplusContainer.replaceChildren(card);
  } else {
    aplusContainer.replaceChildren(createEmptyGenerationPrompt());
  }
}

// ── Export Preview ────────────────────────────────────────────────

export function renderExport(exportText) {
  const exportSection = $('#export-section');
  const exportPreview = $('#export-preview');
  const copyExportBtn = $('#copy-export');
  if (exportSection) exportSection.hidden = false;
  if (exportPreview) {
    if (exportText) {
      exportPreview.replaceChildren(createStep3Element('pre', 'export-pre', exportText));
    } else {
      exportPreview.textContent = '暂无';
    }
  }
  if (copyExportBtn) copyExportBtn.disabled = !exportText;
  const translateBtn = $('#translate-export');
  if (translateBtn) translateBtn.disabled = !exportText;
}

// ── searchTermsPreview getter (for copy handler) ─────────────────

export function getSearchTermsPreviewFromViewModel(viewModel) {
  return viewModel?.searchTerms?.text || '';
}
